import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import type { MainView } from '../../lib/mainView';
import { MAIN_VIEW_DEFAULT } from '../../lib/mainView';
import { isComputersRoute, routes } from '../../lib/routes';
import { useAuthStore, useChannelStore } from '../../stores';

export interface NavigationApi {
  current: MainView;
  push: (view: MainView) => void;
  back: () => void;
  close: () => void;
  canGoBack: boolean;
}

const NavigationContext = createContext<NavigationApi | null>(null);

interface NavigationProviderProps {
  children: React.ReactNode;
  initial?: MainView;
}

function deriveMainView(pathname: string): MainView {
  if (pathname.startsWith('/agents')) return 'agents';
  if (pathname.match(/\/invitations(\/|$)/)) return 'invitations';
  if (isComputersRoute(pathname)) return 'remote-nodes';
  if (pathname.match(/\/s\/[^/]+\/settings(\/|$)/)) return 'server-settings';
  return MAIN_VIEW_DEFAULT;
}

export function NavigationProvider({ children }: NavigationProviderProps) {
  return <NavigationContext.Provider value={null}>{children}</NavigationContext.Provider>;
}

export function useNavigation(): NavigationApi {
  const navigate = useNavigate();
  const location = useLocation();
  const guildId = useAuthStore((s) => s.guildId);
  const currentChannelId = useChannelStore((s) => s.currentChannelId);
  const current = deriveMainView(location.pathname);

  const push = useCallback(
    (view: MainView) => {
      switch (view) {
        case 'agents':
          navigate(routes.agents());
          break;
        case 'invitations':
          navigate(routes.invitations(guildId!));
          break;
        case 'remote-nodes':
          navigate(routes.computers());
          break;
        case 'settings':
        case 'server-settings':
          navigate(routes.serverSettings(guildId!));
          break;
        case 'channel':
        default:
          if (currentChannelId) {
            navigate(routes.channel(guildId!, currentChannelId));
          }
          break;
      }
    },
    [navigate, currentChannelId, guildId],
  );

  const back = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else if (currentChannelId) {
      navigate(routes.channel(guildId!, currentChannelId), { replace: true });
    } else {
      navigate(routes.root(), { replace: true });
    }
  }, [navigate, currentChannelId, guildId]);

  const close = useCallback(() => {
    if (currentChannelId) {
      navigate(routes.channel(guildId!, currentChannelId));
    } else {
      navigate(routes.root());
    }
  }, [navigate, currentChannelId, guildId]);

  return useMemo<NavigationApi>(
    () => ({
      current,
      push,
      back,
      close,
      canGoBack: current !== MAIN_VIEW_DEFAULT,
    }),
    [current, push, back, close],
  );
}
